import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, CardContent, Button, TextField, Dialog, DialogTitle,
  DialogContent, DialogActions, Chip, IconButton, Switch, Tabs, Tab,
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText, OutlinedInput,
  Divider
} from '@mui/material';
import { Add, Edit, School, People, Delete, Mic } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { api } from '../api';

export default function ClassesPage() {
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [deletingClass, setDeletingClass] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [enrollingClass, setEnrollingClass] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [newStudentName, setNewStudentName] = useState('');
  const [newStudentRate, setNewStudentRate] = useState('');
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0); // 0 = Active, 1 = Archived

  const fetchClasses = async () => {
    try {
      const [classesData, studentsData] = await Promise.all([api.getClasses(), api.getStudents()]);
      setClasses(classesData);
      setAllStudents(studentsData);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchClasses(); }, []);

  const activeClasses = classes.filter(c => c.is_active);
  const archivedClasses = classes.filter(c => !c.is_active);
  const displayedClasses = tab === 0 ? activeClasses : archivedClasses;

  const openDialog = (cls = null) => {
    if (cls) {
      setEditingClass(cls);
      setForm({ name: cls.name, description: cls.description || '' });
    } else {
      setEditingClass(null);
      setForm({ name: '', description: '' });
    }
    setDialogOpen(true);
  };

  const openEnrollDialog = async (cls) => {
    setEnrollingClass(cls);
    setNewStudentName('');
    setNewStudentRate('');
    try {
      const detail = await api.getClass(cls.id);
      setSelectedStudents((detail.students || []).map(s => s.id));
    } catch { setSelectedStudents([]); }
    setEnrollDialogOpen(true);
  };

  const handleSave = async () => {
    const payload = {
      name: form.name,
      description: form.description,
    };
    try {
      if (editingClass) {
        await api.updateClass(editingClass.id, payload);
      } else {
        await api.createClass(payload);
      }
      setDialogOpen(false);
      fetchClasses();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleAddNewStudent = async () => {
    if (!newStudentName.trim()) return;
    try {
      const created = await api.createStudent({ name: newStudentName.trim(), rate_per_class: parseFloat(newStudentRate) || 0 });
      setAllStudents(prev => [...prev, created]);
      setSelectedStudents(prev => [...prev, created.id]);
      setNewStudentName('');
      setNewStudentRate('');
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEnrollSave = async () => {
    try {
      const detail = await api.getClass(enrollingClass.id);
      const currentStudentIds = (detail.students || []).map(s => s.id);
      for (const sid of currentStudentIds) {
        if (!selectedStudents.includes(sid)) {
          await api.unenrollStudent(sid, enrollingClass.id);
        }
      }
      for (const sid of selectedStudents) {
        if (!currentStudentIds.includes(sid)) {
          await api.enrollStudent(sid, { class_id: enrollingClass.id });
        }
      }
      setEnrollDialogOpen(false);
      fetchClasses();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleToggle = async (cls) => {
    await api.updateClass(cls.id, { is_active: cls.is_active ? 0 : 1 });
    fetchClasses();
  };

  const handleDelete = async () => {
    if (!deletingClass) return;
    try {
      await api.deleteClass(deletingClass.id);
      setConfirmDeleteOpen(false);
      setDeletingClass(null);
      fetchClasses();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" fontWeight={700}>Classes</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={() => openDialog()}>
          New Class
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`Active (${activeClasses.length})`} />
        <Tab label={`Archived (${archivedClasses.length})`} />
      </Tabs>

      {displayedClasses.length === 0 && !loading && (
        <Card variant="outlined" sx={{ textAlign: 'center', py: 4 }}>
          <School sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
          <Typography color="text.secondary">
            {tab === 0 ? 'No active classes. Create your first class!' : 'No archived classes.'}
          </Typography>
        </Card>
      )}

      {displayedClasses.map(cls => (
        <Card key={cls.id} sx={{ mb: 1.5 }}>
          <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>{cls.name}</Typography>
                {cls.description && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3 }}>
                    {cls.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  <Chip size="small" icon={<People />} label={`${cls.student_count || 0} students`} variant="outlined" color="primary" />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton size="small" onClick={() => navigate(`/classes/${cls.id}/recordings`)} title="Recordings"><Mic fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => openEnrollDialog(cls)} title="Manage Students"><People fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => openDialog(cls)} title="Edit"><Edit fontSize="small" /></IconButton>
                <IconButton size="small" onClick={() => { setDeletingClass(cls); setConfirmDeleteOpen(true); }} title="Delete" color="error"><Delete fontSize="small" /></IconButton>
                <Switch size="small" checked={!!cls.is_active} onChange={() => handleToggle(cls)} title={cls.is_active ? 'Archive' : 'Restore'} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      ))}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingClass ? 'Edit Class' : 'New Class'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth label="Class Name" value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            sx={{ mt: 1, mb: 2 }} placeholder="e.g., Music Level 1"
            autoFocus required
          />
          <TextField
            fullWidth label="Description (optional)" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            sx={{ mb: 2 }} multiline rows={2}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim()}>
            {editingClass ? 'Save' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Students Dialog */}
      <Dialog open={enrollDialogOpen} onClose={() => setEnrollDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Manage Students — {enrollingClass?.name}</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Students</InputLabel>
            <Select
              multiple value={selectedStudents}
              onChange={e => setSelectedStudents(e.target.value)}
              input={<OutlinedInput label="Students" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map(id => {
                    const s = allStudents.find(st => st.id === id);
                    return <Chip key={id} label={s?.name || id} size="small" />;
                  })}
                </Box>
              )}
            >
              {allStudents.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  <Checkbox checked={selectedStudents.includes(s.id)} />
                  <ListItemText primary={s.name} secondary={!s.is_active ? 'Inactive — will be re-enabled' : ''} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Divider sx={{ my: 2 }} />
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Student not listed? Add a new one:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small" sx={{ flex: 2 }} label="Student Name" value={newStudentName}
              onChange={e => setNewStudentName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewStudent(); } }}
            />
            <TextField
              size="small" sx={{ flex: 1 }} label="$/class" value={newStudentRate}
              onChange={e => setNewStudentRate(e.target.value)}
              type="number"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddNewStudent(); } }}
            />
            <Button variant="outlined" onClick={handleAddNewStudent} disabled={!newStudentName.trim()} sx={{ minWidth: 60, mt: '2px' }}>
              Add
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEnrollDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleEnrollSave}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete Class</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete <strong>{deletingClass?.name}</strong>?
            This will also delete all sessions, attendance records, and enrollments for this class.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
